'use strict';

/* global angular, Mousetrap */

import omit from 'lodash/omit';

/**
 * Service for handling of keyboard shortcuts. Uses Mousetrap (https://craig.is/killing/mice) internally but manages
 * keyboard shortcut on a stack to make it easy to add and remove shortcuts as the application moves in and out of
 * different contexts.
 *
 * Keyboard shortcuts are managed in groups called ShortcutSets. A ShortcutSet is an object with the
 * following attributes:
 *
 * {
 *   <KEY>: { action: <FUNCTION>, label: <STRING> },
 *   ...,
 *
 *   global: {
 *     <KEY>: { action: <FUNCTION>, label: <STRING> },
 *     ...
 *   }
 * }
 *
 * A <KEY> is a string representing a key or key combination as defined by Mousetrap. By default shortcuts are bound
 * with Mousetrap.bind(...) but any binding found under attribute 'global' will be bound with Mousetrap.bindGlobal().
 *
 * Example:
 *
 * {
 *   'mod+e': {
 *     action: editFn,
 *     label: 'Edit content'
 *   },
 *   'escape': {
 *     action: closeFn,
 *     label: 'Close content'
 *   },
 *
 *   global: {
 *     'mod+l': {
 *       action: searchFn,
 *       label: 'Search'
 *     }
 *   }
 * }
 *
 */
angular.module ('atex.aceui.service').factory ('KeyboardShortcutService',
  ['$translate', function ($translate) {

    let _stack = [], _universals = { global: {} };

    const peek = function (stack) {
      let top = { global: {} };
      if (stack.length > 0) {
        top = stack[stack.length - 1];
      }
      return top;
    };

    const calculateActiveShortcutSet = function (newSet, currentSet) {
      currentSet = currentSet || {};
      var activeShortcutSet = Object.assign ({}, currentSet, newSet, _universals);
      if (currentSet.global && newSet.global) {
        activeShortcutSet.global = Object.assign ({}, currentSet.global, newSet.global, _universals.global);
      } else if (newSet.global) {
        activeShortcutSet.global = Object.assign ({}, newSet.global, _universals.global);
      } else {
        activeShortcutSet.global = Object.assign ({}, _universals.global);
      }
      return activeShortcutSet;
    };

    const bind = function (shortcutSet) {
      const global = shortcutSet.global || {};
      Object.keys (global).forEach (function (key) {
        Mousetrap.bindGlobal (key, global[key].action || global[key]);
      });

      const set = omit(shortcutSet, 'global');
      Object.keys (set).forEach (function (key) {
        Mousetrap.bind (key, set[key].action || set[key]);
      });
    };

    const isMac = () => {
      return navigator.platform.toUpperCase ().includes ('MAC');
    };

    return {

      /**
       * Describes the current active ShortcutSet by returning a mirror object of the ShortcutSet
       * only the values are labels instead of actions.
       */
      describe: function () {
        let result = {};
        const currentShortcutSet = peek (_stack);
        const global = currentShortcutSet.global;
        const set = omit(currentShortcutSet, 'global');
        if (global) {
          result.global = {};
          Object.keys (global).forEach (function (key) {
            result.global[key] = $translate.instant (global[key].label) || '';
          });
        }

        Object.keys (set).forEach (function (key) {
          result[key] = $translate.instant (set[key].label) || '';
        });

        return result;
      },

      /**
       * Hides all current ShortcutsSets on the stack and pushes a new one on top.
       *
       * @param shortcutSet A ShortcutSet
       */
      hideAndPush: function (shortcutSet) {
        var activeShortcutSet = calculateActiveShortcutSet (shortcutSet);
        _stack.push (activeShortcutSet);
        Mousetrap.reset ();
        bind (activeShortcutSet);
      },

      inferUserKey: function (keybShortcutString) {
        // A poor man's solution to the problem...
        let result = keybShortcutString;
        if (isMac ()) {
          result = result
            .replace ('mod', '⌘')
            .replace ('enter', '↩');
        } else {
          result = result
            .replace ('mod', 'ctrl')
            .replace ('enter', '↵');
        }
        result = result.replace ('escape', 'esc');
        result = result.replace ('+', ' + ');
        return result;
      },

      /**
       * Removes the top ShortcutSet, possibly revealing hidden sets beneath it.
       */
      pop: function () {
        _stack.pop ();
        Mousetrap.reset ();

        if (_stack.length > 0) {
          var activeShortcutSet = peek (_stack);
          bind (activeShortcutSet);
        }
      },

      /**
       * Pushes a ShortcutSet on top of the stack.
       *
       * Bindings in the ShortcutSet that already exists on the stack will be hidden.
       *
       * @param shortcutSet A new ShortcutSet
       */
      push: function (shortcutSet) {
        var activeShortcutSet = calculateActiveShortcutSet (shortcutSet, peek (_stack));
        _stack.push (activeShortcutSet);
        Mousetrap.reset ();
        bind (activeShortcutSet);
      },

      /**
       * Removes all ShortcutSets from the stack.
       */
      reset: function () {
        _stack = [];
        Mousetrap.reset ();
      },

      /**
       * Universal sets cannot be overridden, nor hidden, nor deleted.
       */
      universalSet: function (universalSet) {
        _universals = Object.assign ({}, universalSet, _universals);
        _universals.global = Object.assign ({}, universalSet.global || {}, _universals.global);
        var activeShortcutSet = calculateActiveShortcutSet ({}, peek (_stack));
        Mousetrap.reset ();
        bind (activeShortcutSet);
      }
    };
  }]
);